import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useMemo } from 'react'
import { AppSidebar, type SidebarNavItem } from '@/components/business/app-sidebar'
import { AppHeader } from '@/components/business/app-header'
import { usePageHeaderState } from '@/components/business/page-header-context'
import { useAuthStore } from '@/stores/auth'
import { useConsultUnreadCount } from '@/features/consult/api'
import { ConsultMessageNotifier } from '@/features/consult/components/consult-message-notifier'
import { NotificationGuidedTour } from '@/features/notification/components/notification-guided-tour'
import { useResponsiveSidebar } from '@/hooks/use-responsive-sidebar'
import { useMenuLogoUrl, useSiteTitle } from '@/features/site/api'
import { toast } from 'sonner'

// 律师端主导航展示工作台与消息; "我的" 放到底部账号菜单里.
// "AI 文书 / 律师列表" 是用户端的入口, 律师端不需要.
// "新建对话" CTA 也一并去掉, 因为律师不主动发起新会话 (会话由客户咨询触发).
const NAV_ITEMS: SidebarNavItem[] = [
  { to: '/lawyer', label: '工作台', icon: 'dashboard' },
  { to: '/lawyer/consult', label: '消息', icon: 'chat' },
]

const TITLE_MAP: Record<string, string> = {
  '/lawyer': '工作台',
  '/lawyer/consult': '消息',
  '/lawyer/settings': '设置',
  '/lawyer/ai-doc': 'AI 文书',
  '/lawyer/profile/edit': '编辑资料',
}

/**
 * 律师端布局
 *
 * 设计参考: ui/.../dashboard 的整体结构 (320px sidebar + h-16 header).
 * 与 UserLayout 共用 AppSidebar / AppHeader, 但导航/CTA 与用户端不同.
 */
export function LawyerLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { sidebarCollapsed, setSidebarCollapsed, closeSidebarOnMobile } = useResponsiveSidebar()
  const siteTitle = useSiteTitle()
  const menuLogoUrl = useMenuLogoUrl()
  const pageHeader = usePageHeaderState()
  const userInfo = useAuthStore((s) => s.userInfo)
  const logout = useAuthStore((s) => s.logout)
  const accountName = userInfo?.nickname || userInfo?.username || '律师'
  const consultUnreadQuery = useConsultUnreadCount('lawyer', { refetchInterval: 60_000 })
  const consultUnreadCount = consultUnreadQuery.data ?? 0
  const navItems = useMemo<SidebarNavItem[]>(
    () =>
      NAV_ITEMS.map((item) =>
        item.to === '/lawyer/consult' ? { ...item, badgeCount: consultUnreadCount } : item,
      ),
    [consultUnreadCount],
  )
  const routeTitle =
    TITLE_MAP[location.pathname] ??
    Object.entries(TITLE_MAP)
      .filter(([k]) => location.pathname.startsWith(k + '/'))
      .sort((a, b) => b[0].length - a[0].length)[0]?.[1] ??
    '律师工作台'
  // 页面通过 usePageHeader 上报的标题/返回优先; AI 文书页保留原有兜底.
  const headerTitle =
    pageHeader.title ?? (location.pathname === '/lawyer/ai-doc' ? routeTitle : undefined)
  const headerOnBack = pageHeader.back
  const isConsultRoute = location.pathname === '/lawyer/consult'

  return (
    <div className="text-on-surface flex h-dvh overflow-hidden">
      <ConsultMessageNotifier />
      {!sidebarCollapsed && (
        <button
          type="button"
          aria-label="关闭侧边栏"
          className="bg-inverse-surface/30 fixed inset-0 z-30 backdrop-blur-[18px] backdrop-saturate-150 md:hidden"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}
      <AppSidebar
        brandTitle={siteTitle}
        brandIcon={menuLogoUrl}
        brandSubtitle="律师工作台"
        collapsed={sidebarCollapsed}
        onNavigate={closeSidebarOnMobile}
        items={navItems}
        showHistory={false}
        account={{
          name: accountName,
          avatar: userInfo?.avatar,
          switchEntry: {
            label: '切换到用户端',
            icon: 'house',
            onClick: () => navigate('/u'),
          },
          onProfile: () => navigate('/lawyer'),
          onSettings: () => navigate('/lawyer/settings'),
          onLogout: () => {
            logout()
            toast.success('已退出登录')
            navigate('/', { replace: true })
          },
        }}
      />
      <main className="app-page-surface mx-auto flex min-h-0 min-w-0 flex-1 flex-col">
        <AppHeader
          title={headerTitle}
          titleHref={pageHeader.titleHref}
          subtitle={pageHeader.subtitle}
          subtitleDetails={pageHeader.subtitleDetails}
          onBack={headerOnBack}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={() => setSidebarCollapsed((collapsed) => !collapsed)}
        />
        <div className="app-page-surface min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>
      <NotificationGuidedTour open={isConsultRoute} />
    </div>
  )
}
